import styled from 'styled-components';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';

import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { useTranslations } from '../../../hooks/useTranslations';
import { TakeoverIntent } from '../../../types';
import { hasNewUploads, hasEditChanges } from '../../../utils/images';
import UploadButtonCombo, { UploadComboConfig } from '../../UploadButtonCombo';
import { Image24 } from 'ui/icons';
import { FlexSpacer } from 'ui/flexSpacer';
import { spacing } from 'ui/theme';

const ButtonContainer = styled.div<{ $isComboButtonVisible: boolean }>`
  display: flex;
  justify-content: ${({ $isComboButtonVisible }) => ($isComboButtonVisible ? 'space-between' : 'flex-end')};
  column-gap: ${({ theme }) => theme.spacing.values.half};
  width: 100%;
  padding: ${spacing.half};
`;

export const testIds = {
  btnCancel: 'cl-index-takeover-btn-cancel',
  btnSave: 'cl-index-takeover-btn-save',
  btnUpload: 'cl-index-takeover-btn-upload',
  container: 'cl-index-takeover-footer-container',
};

export const TakeoverFooter = () => {
  const { t } = useTranslations();
  const {
    clearPendingUploads,
    handleSaveEdits,
    handleUploadPending,
    pendingImages,
    pendingUploadCount,
  } = useUploadAssetsContext();
  const { onClose, currentIntent, setCurrentIntent, onImportFromUrl } = useTakeoverContext();
  const isEditMode = currentIntent === TakeoverIntent.EDIT;
  const warnBeforeClose = isEditMode ? hasEditChanges(pendingImages) : hasNewUploads(pendingImages);
  const isComboButtonVisible = currentIntent === TakeoverIntent.UPLOAD;
  const isUploadButtonDisabled =
    !pendingUploadCount ||
    currentIntent === TakeoverIntent.IMPORT_FROM_URL ||
    currentIntent === TakeoverIntent.BROWSE_FILES;

  const uploadComboConfig: UploadComboConfig = {
    label: (
      <FlexSpacer>
        <Image24 />
        {t('button.browseFiles')}
      </FlexSpacer>
    ),
    onClick: () => {
      setCurrentIntent(TakeoverIntent.BROWSE_FILES);
    },
    actions: [
      {
        content: t('menu.linkRemote'),
        id: 'btn-combo-import-from-url',
        onClick: onImportFromUrl,
      },
    ],
  };

  const handleCancel = () => {
    if (
      pendingImages.length > 0 &&
      (currentIntent === TakeoverIntent.IMPORT_FROM_URL ||
        currentIntent === TakeoverIntent.BROWSE_FILES)
    ) {
      setCurrentIntent(TakeoverIntent.UPLOAD);
      return;
    }
    onClose(warnBeforeClose, clearPendingUploads);
  };

  return (
    <ButtonContainer data-test={testIds.container} $isComboButtonVisible={isComboButtonVisible}>
      {isComboButtonVisible && (
        <UploadButtonCombo config={uploadComboConfig} variant={BUTTON_VARIANTS.SECONDARY} />
      )}
      <FlexSpacer>
        <Button
          data-test={testIds.btnCancel}
          onClick={handleCancel}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.GHOST}
        >
          <span>{t('button.cancel')}</span>
        </Button>
        {currentIntent === TakeoverIntent.EDIT ? (
          <Button
            data-test={testIds.btnSave}
            isDisabled={false}
            onClick={() => handleSaveEdits()}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            <span>{t('button.save')}</span>
          </Button>
        ) : (
          <Button
            data-test={testIds.btnUpload}
            isDisabled={isUploadButtonDisabled}
            onClick={() => handleUploadPending()}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            <span>{t('button.uploadImages', { count: pendingUploadCount })}</span>
          </Button>
        )}
      </FlexSpacer>
    </ButtonContainer>
  );
};
